import React from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle } from "lucide-react";

interface PendingApprovalsCardProps {
  approvals: any[];
  activeTab: string;
  setActiveTab: (tab: string) => void;
}

const PendingApprovalsCard: React.FC<PendingApprovalsCardProps> = ({
  approvals,
  activeTab,
  setActiveTab,
}) => {
  const navigate = useNavigate();

  // Filter data based on tab
  const filtered = approvals.filter((item: any) =>
    activeTab === "vendors"
      ? item.status === "pending"
      : item.user?.is_active === 0
  );

  return (
    <div className="bg-white rounded-xl shadow-md p-4 mt-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-2 border-b border-gray-300 pb-2">
        <h2 className="text-lg font-semibold text-black">Pending Approvals</h2>
        <button
          onClick={() => navigate("/approvals")}
          className="text-blue-600 text-sm font-medium"
        >
          View All
        </button>
      </div>

      {/* Tabs */}
      <div className="flex space-x-4 border-b border-gray-200 mb-4">
        {["veterinarians", "service_providers", "vendors"].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`py-2 px-4 text-sm font-medium ${
              activeTab === tab
                ? "border-b-2 border-blue-600 text-blue-600"
                : "text-gray-500"
            }`}
          >
            {tab
              .replace("_", " ")
              .replace(/\b\w/g, (char) => char.toUpperCase())}
          </button>
        ))}
      </div>

      {/* Table */}
      <table className="w-full text-sm text-left">
        <thead>
          <tr className="text-gray-500">
            {activeTab === "vendors" && (
              <>
                <th className="py-2">Store Name</th>
                <th>Username</th>
                <th>Email</th>
                <th>Registered</th>
                <th>Status</th>
              </>
            )}

            {activeTab === "veterinarians" && (
              <>
                <th className="py-2">Name</th>
                <th>Speciality</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>Status</th>
              </>
            )}

            {activeTab === "service_providers" && (
              <>
                <th className="py-2">Name</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>Status</th>
              </>
            )}
          </tr>
        </thead>

        <tbody>
          {filtered.length === 0 ? (
            <tr>
              <td
                colSpan={activeTab === "vendors" ? 5 : activeTab === "veterinarians" ? 5 : 4}
                className="py-8 text-center text-gray-500"
              >
                <div className="flex flex-col items-center space-y-2">
                  <AlertCircle size={40} />
                  <span>No approval found</span>
                </div>
              </td>
            </tr>
          ) : (
            filtered.slice(0, 5).map((item: any, index: number) => (
              <tr
                key={index}
                className="border-t cursor-pointer hover:bg-gray-100"
                onClick={() => {
                  let path = "";
                  if (activeTab === "veterinarians") {
                    path = `/veterinarians/details/${item.id}`;
                  } else if (activeTab === "service_providers") {
                    path = `/service-providers/details/${item.id}`;
                  } else if (activeTab === "vendors") {
                    path = `/vendor/details/${item.id}`;
                  }
                  navigate(path, { state: { item, type: "approve", fromTab: activeTab } });
                }}
              >
                {/* Vendors */}
                {activeTab === "vendors" && (
                  <>
                    <td className="py-2 text-gray-900 font-medium">
                      {item.store_name || item.name}
                    </td>
                    <td className="py-2 text-gray-700">{item.username || "N/A"}</td>
                    <td className="py-2 text-gray-700">{item.email || "N/A"}</td>
                    <td className="py-2 text-gray-700">
                      {item.registered
                        ? new Date(item.registered).toLocaleDateString()
                        : "N/A"}
                    </td>
                    <td className="py-2 text-gray-700 flex items-center gap-2">
                      <span
                        className={`h-2 w-2 rounded-full animate-pulse ${
                          item.status === "pending" ? "bg-red-500" : "bg-green-500"
                        }`}
                      ></span>
                      <span>{item.status || "Unknown"}</span>
                    </td>
                  </>
                )}

                {/* Veterinarians */}
                {activeTab === "veterinarians" && (
                  <>
                    <td className="py-2 text-gray-900 font-medium">
                      {`${item.user?.first_name ?? ""} ${item.user?.last_name ?? ""}`}
                    </td>
                    <td className="py-2 text-gray-700">
                      {item.provider_speciality?.length
                        ? item.provider_speciality
                            .map((spec: { speciality: string }) => spec.speciality)
                            .join(", ")
                        : "N/A"}
                    </td>
                    <td className="py-2 text-gray-700">{item.user?.email || "N/A"}</td>
                    <td className="py-2 text-gray-700">{item.user?.mobile || "N/A"}</td>
                    <td className="py-2 text-gray-700 flex items-center gap-2">
                      <span
                        className={`h-2 w-2 rounded-full animate-pulse ${
                          item.user?.is_active === 0 ? "bg-red-500" : "bg-green-500"
                        }`}
                      ></span>
                      <span>{item.user?.is_active === 0 ? "Inactive" : "Active"}</span>
                    </td>
                  </>
                )}

                {/* Service Providers */}
                {activeTab === "service_providers" && (
                  <>
                    <td className="py-2 text-gray-900 font-medium">
                      {`${item.user?.first_name ?? ""} ${item.user?.last_name ?? ""}`}
                    </td>
                    <td className="py-2 text-gray-700">{item.user?.email || "N/A"}</td>
                    <td className="py-2 text-gray-700">{item.user?.mobile || "N/A"}</td>
                    <td className="py-2 text-gray-700 flex items-center gap-2">
                      <span
                        className={`h-2 w-2 rounded-full animate-pulse ${
                          item.user?.is_active === 0 ? "bg-red-500" : "bg-green-500"
                        }`}
                      ></span>
                      <span>{item.user?.is_active === 0 ? "Inactive" : "Active"}</span>
                    </td>
                  </>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default PendingApprovalsCard;
